import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/shared/components/Input';
import { Select } from '@/shared/components/Select';
import { Textarea } from '@/shared/components/Textarea';
import { Button } from '@/shared/components/Button';
import { ApiError } from '@/lib/api-client';
import { useProfessors } from '@/features/director-professors/hooks/useProfessors';
import { useSubjects } from '@/features/director-professors/hooks/useSubjects';
import { useAddClass } from '../hooks/useSchedules';
export function AddClassForm({ scheduleId, oppositionId, month, year, defaultDate, onSuccess }) {
    const [serverError, setServerError] = useState('');
    const { data: subjects = [], isLoading: subjectsLoading } = useSubjects(oppositionId);
    const { data: professors = [], isLoading: professorsLoading } = useProfessors();
    const addClassMutation = useAddClass(scheduleId);
    const { register, handleSubmit, watch, formState: { errors } } = useForm({
        defaultValues: {
            subject_id: '',
            professor_id: '',
            date: defaultDate ?? '',
            start_time: '',
            end_time: '',
            classroom: '',
            notes: '',
        },
    });
    const selectedSubjectId = watch('subject_id');
    // Limit professors to those that (a) belong to this opposition and (b) teach
    // the chosen subject (when one is selected). The backend would accept any
    // FK-valid professor, but the academy's flow expects a match.
    const eligibleProfessors = useMemo(() => {
        return professors.filter((p) => {
            if (p.opposition_id !== oppositionId)
                return false;
            if (!selectedSubjectId)
                return true;
            return p.subjects.some((s) => s.id === selectedSubjectId);
        });
    }, [professors, oppositionId, selectedSubjectId]);
    const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    function onSubmit(values) {
        setServerError('');
        if (!values.subject_id || !values.professor_id) {
            setServerError('Selecciona materia y profesor.');
            return;
        }
        const dto = {
            subject_id: values.subject_id,
            professor_id: values.professor_id,
            date: values.date,
            start_time: values.start_time,
            end_time: values.end_time,
            ...(values.classroom.trim() ? { classroom: values.classroom.trim() } : {}),
            ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
        };
        addClassMutation.mutate(dto, {
            onSuccess: () => onSuccess(),
            onError: (err) => {
                setServerError(err instanceof ApiError ? err.message : 'Ha ocurrido un error.');
            },
        });
    }
    const subjectPlaceholder = subjectsLoading
        ? 'Cargando…'
        : subjects.length === 0
            ? 'Crea materias para esta oposición primero'
            : 'Seleccionar materia';
    const professorPlaceholder = professorsLoading
        ? 'Cargando…'
        : eligibleProfessors.length === 0
            ? selectedSubjectId
                ? 'Ningún profesor de esta oposición imparte esta materia'
                : 'No hay profesores en esta oposición'
            : 'Seleccionar profesor';
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "flex flex-col gap-4", noValidate: true, children: [_jsx(Select, { label: "Materia", disabled: subjectsLoading || subjects.length === 0, options: [
                    { value: '', label: subjectPlaceholder },
                    ...subjects.map((s) => ({ value: s.id, label: s.name })),
                ], error: errors.subject_id?.message, ...register('subject_id', { required: 'La materia es obligatoria' }) }), _jsx(Select, { label: "Profesor", disabled: professorsLoading || eligibleProfessors.length === 0, options: [
                    { value: '', label: professorPlaceholder },
                    ...eligibleProfessors.map((p) => ({
                        value: p.user_profile_id,
                        label: `${p.first_name} ${p.last_name}`,
                    })),
                ], error: errors.professor_id?.message, ...register('professor_id', { required: 'El profesor es obligatorio' }) }), _jsx(Input, { label: "Fecha", type: "date", min: monthStart, max: monthEnd, error: errors.date?.message, hint: `Solo fechas dentro del mes ${month}/${year}`, ...register('date', { required: 'La fecha es obligatoria' }) }), _jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(Input, { label: "Hora inicio", type: "time", error: errors.start_time?.message, ...register('start_time', { required: 'La hora de inicio es obligatoria' }) }), _jsx(Input, { label: "Hora fin", type: "time", error: errors.end_time?.message, ...register('end_time', { required: 'La hora de fin es obligatoria' }) })] }), _jsx(Input, { label: "Aula (opcional)", ...register('classroom') }), _jsx(Textarea, { label: "Notas (opcional)", rows: 2, ...register('notes') }), serverError && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: serverError })), _jsx(Button, { type: "submit", loading: addClassMutation.isPending, className: "w-full", children: "A\u00F1adir clase" })] }));
}
//# sourceMappingURL=AddClassForm.js.map